import { EPSILON, vec, type Vec2 } from '../math';

import * as intersect from './intersect';
import * as polygonOps from './polygon';
import { type Polygon, type Segment } from './types';

/**
 * Polygon boolean operations: union, intersection and difference.
 *
 * ## Why an arrangement rather than Greiner–Hormann
 *
 * The classic vertex-walking algorithms (Greiner–Hormann, Weiler–Atherton) are elegant on
 * clean input and treacherous on the input a classroom actually produces. Their entry/exit
 * bookkeeping assumes intersections are transversal, so a shared edge, a vertex lying on
 * the other polygon's edge, or two rings touching at a single point each need a special
 * case, and those special cases interact.
 *
 * This builds the planar arrangement instead:
 *
 * 1. Split every edge of each polygon at every crossing with the other.
 * 2. Classify each resulting piece by its midpoint — inside, outside, or on the boundary.
 * 3. Keep the pieces the operation calls for.
 * 4. Chain them back into rings.
 *
 * The degenerate cases stop being special. A vertex on an edge is just a split point. A
 * shared edge is a piece classified `on`, and the operation decides what to do with it
 * from the two polygons' directions of travel. Nothing depends on a crossing being clean.
 *
 * ## The cost, and why it is acceptable here
 *
 * Splitting is O(n·m): every edge is tested against every edge of the other polygon. A
 * sweep line would make it O((n+m) log(n+m)), and for the polygons this editor deals with —
 * a triangle, a quadrilateral, occasionally a regular polygon with twenty sides — the
 * sweep's constant factor and its own degenerate handling cost more than the quadratic
 * loop saves. If a use ever arrives with thousands of vertices, this is the function to
 * replace, and its tests will still apply.
 *
 * ## Output convention
 *
 * A list of rings. Outer boundaries run counter-clockwise, holes run clockwise — the usual
 * convention, and the one `signedArea` already agrees with, so the total signed area of the
 * result equals the area the operation should produce. An empty list means the result is
 * empty, which is a real answer: two disjoint polygons have no intersection.
 */

export type BooleanOp = 'union' | 'intersection' | 'difference';

/** Where a piece of one polygon's boundary lies relative to the other polygon. */
type Placement = 'inside' | 'outside' | 'on-same' | 'on-opposite';

interface Piece {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly placement: Placement;
}

export function union(a: Polygon, b: Polygon, epsilon: number = EPSILON): readonly Polygon[] {
  return polygonBoolean(a, b, 'union', epsilon);
}

export function intersection(
  a: Polygon,
  b: Polygon,
  epsilon: number = EPSILON
): readonly Polygon[] {
  return polygonBoolean(a, b, 'intersection', epsilon);
}

/** `a` minus `b`. Not symmetric; the result can contain holes. */
export function difference(a: Polygon, b: Polygon, epsilon: number = EPSILON): readonly Polygon[] {
  return polygonBoolean(a, b, 'difference', epsilon);
}

export function polygonBoolean(
  a: Polygon,
  b: Polygon,
  op: BooleanOp,
  epsilon: number = EPSILON
): readonly Polygon[] {
  // Orientation is normalised first so that "same direction" below is a statement about
  // the geometry rather than about how the user happened to wind the input.
  const first = polygonOps.toCounterClockwise(dedupe(a, epsilon));
  const second = polygonOps.toCounterClockwise(dedupe(b, epsilon));

  if (first.length < 3 || second.length < 3) return [];

  const fromA = piecesOf(first, second, epsilon);
  const fromB = piecesOf(second, first, epsilon);

  const kept: Piece[] = [];

  for (const piece of fromA) {
    if (keepsFromFirst(op, piece.placement)) kept.push(piece);
  }

  for (const piece of fromB) {
    if (!keepsFromSecond(op, piece.placement)) continue;
    // Difference reverses what it takes from `b`: the hole must wind opposite to the
    // boundary that contains it, and the reversal is what makes the signed areas cancel.
    kept.push(op === 'difference' ? { ...piece, from: piece.to, to: piece.from } : piece);
  }

  return chain(kept, epsilon);
}

/**
 * Which pieces of the first polygon's boundary survive.
 *
 * A shared edge travelled the same way by both polygons is on the outside of the union and
 * the inside of the intersection, so exactly one copy is taken — from `a`, arbitrarily but
 * consistently. Taking both would produce a doubled edge that the chaining step could not
 * resolve.
 */
function keepsFromFirst(op: BooleanOp, placement: Placement): boolean {
  switch (op) {
    case 'union':
      return placement === 'outside' || placement === 'on-same';
    case 'intersection':
      return placement === 'inside' || placement === 'on-same';
    case 'difference':
      return placement === 'outside' || placement === 'on-opposite';
  }
}

/** Which pieces of the second polygon's boundary survive. Shared edges are never taken twice. */
function keepsFromSecond(op: BooleanOp, placement: Placement): boolean {
  switch (op) {
    case 'union':
      return placement === 'outside';
    case 'intersection':
      return placement === 'inside';
    case 'difference':
      return placement === 'inside';
  }
}

/* ------------------------------------------------------------------- splitting */

/**
 * Splits every edge of `subject` where `clip` crosses or touches it, and classifies each
 * resulting piece.
 */
function piecesOf(subject: Polygon, clip: Polygon, epsilon: number): readonly Piece[] {
  const pieces: Piece[] = [];

  for (const edge of polygonOps.edges(subject)) {
    for (const [from, to] of splitEdge(edge, clip, epsilon)) {
      // A split can leave a zero-length remnant where an edge ends exactly on a crossing.
      if (vec.distance(from, to) <= epsilon) continue;
      pieces.push({ from, to, placement: placeOf(from, to, clip, epsilon) });
    }
  }

  return pieces;
}

/** The sub-segments of `edge`, cut at every point where `clip`'s boundary meets it. */
function splitEdge(edge: Segment, clip: Polygon, epsilon: number): readonly [Vec2, Vec2][] {
  const cuts: Vec2[] = [edge.a, edge.b];

  for (const other of polygonOps.edges(clip)) {
    const crossing = intersect.segmentSegment(edge, other, epsilon);
    if (crossing !== null) cuts.push(crossing);

    /*
     * Collinear overlaps produce no crossing point, because two overlapping segments meet
     * in a segment rather than a point. Their endpoints are still cut points: they are
     * exactly where the shared stretch begins and ends, and without them a piece would
     * straddle the boundary and be classified by a midpoint that means nothing.
     */
    for (const endpoint of [other.a, other.b]) {
      if (isOnSegment(edge, endpoint, epsilon)) cuts.push(endpoint);
    }
  }

  const ordered = orderAlong(edge, cuts, epsilon);
  const result: [Vec2, Vec2][] = [];
  for (let i = 1; i < ordered.length; i++) {
    const from = ordered[i - 1];
    const to = ordered[i];
    if (from !== undefined && to !== undefined) result.push([from, to]);
  }
  return result;
}

/** Sorts cut points along the edge and removes duplicates. */
function orderAlong(edge: Segment, points: readonly Vec2[], epsilon: number): readonly Vec2[] {
  const direction = vec.subtract(edge.b, edge.a);
  const lengthSquared = vec.lengthSquared(direction);
  if (lengthSquared <= epsilon * epsilon) return [edge.a];

  const withT = points.map((point) => ({
    point,
    t: vec.dot(vec.subtract(point, edge.a), direction) / lengthSquared,
  }));

  withT.sort((left, right) => left.t - right.t);

  const result: Vec2[] = [];
  for (const { point, t } of withT) {
    if (t < -epsilon || t > 1 + epsilon) continue;
    const last = result[result.length - 1];
    if (last !== undefined && vec.distance(last, point) <= epsilon) continue;
    result.push(point);
  }
  return result;
}

/**
 * Where a piece lies relative to the clip polygon.
 *
 * The midpoint decides it. A piece produced by `splitEdge` never straddles the boundary —
 * that is exactly what the splitting guarantees — so one interior sample settles the whole
 * piece, and the awkward question of what a vertex is "inside" never arises.
 */
function placeOf(from: Vec2, to: Vec2, clip: Polygon, epsilon: number): Placement {
  const middle = vec.midpoint(from, to);

  if (polygonOps.isOnBoundary(clip, middle, epsilon)) {
    // Shared stretch. Which way the clip runs through it decides whether the two polygons
    // agree here, and that is what tells union from difference.
    const direction = vec.subtract(to, from);
    for (const edge of polygonOps.edges(clip)) {
      if (!isOnSegment(edge, middle, epsilon)) continue;
      const other = vec.subtract(edge.b, edge.a);
      if (vec.isZero(other)) continue;
      return vec.dot(direction, other) > 0 ? 'on-same' : 'on-opposite';
    }
    return 'on-same';
  }

  return polygonOps.containsPoint(clip, middle, epsilon) ? 'inside' : 'outside';
}

function isOnSegment(s: Segment, point: Vec2, epsilon: number): boolean {
  const direction = vec.subtract(s.b, s.a);
  const lengthSquared = vec.lengthSquared(direction);
  if (lengthSquared <= epsilon * epsilon) return vec.distance(s.a, point) <= epsilon;

  const t = vec.dot(vec.subtract(point, s.a), direction) / lengthSquared;
  if (t < -epsilon || t > 1 + epsilon) return false;

  const closest = vec.add(s.a, vec.scale(direction, Math.max(0, Math.min(1, t))));
  return vec.distance(closest, point) <= epsilon;
}

/* -------------------------------------------------------------------- chaining */

/**
 * Joins kept pieces end-to-end into closed rings.
 *
 * Where several pieces leave the same vertex — which happens whenever the two boundaries
 * touch there — the one turning furthest to the right is taken. That is the standard
 * face-tracing rule: hugging the rightmost turn keeps the walk on the boundary of a single
 * face instead of cutting across it into another.
 */
function chain(pieces: readonly Piece[], epsilon: number): readonly Polygon[] {
  const remaining = new Set(pieces.keys());
  const startingAt = new Map<string, number[]>();

  pieces.forEach((piece, index) => {
    const key = keyOf(piece.from, epsilon);
    const bucket = startingAt.get(key);
    if (bucket === undefined) startingAt.set(key, [index]);
    else bucket.push(index);
  });

  const rings: Polygon[] = [];

  while (remaining.size > 0) {
    const first = remaining.values().next().value;
    if (first === undefined) break;

    const ring: Vec2[] = [];
    let index: number | undefined = first;
    let previous: Piece | null = null;

    while (index !== undefined && remaining.has(index)) {
      const piece = pieces[index];
      if (piece === undefined) break;

      remaining.delete(index);
      ring.push(piece.from);

      const candidates = (startingAt.get(keyOf(piece.to, epsilon)) ?? []).filter((candidate) =>
        remaining.has(candidate)
      );
      index = pickNext(pieces, piece, candidates);
      previous = piece;
    }

    // A ring that never closed means the pieces did not form a boundary — a degenerate
    // input rather than a valid face. Dropping it is better than emitting a torn outline.
    const closed =
      ring.length >= 3 &&
      previous !== null &&
      vec.distance(previous.to, ring[0] ?? previous.to) <= Math.max(epsilon, 1e-6);

    if (closed) rings.push(ring);
  }

  return rings;
}

/** The continuation that turns furthest right, keeping the walk on one face. */
function pickNext(
  pieces: readonly Piece[],
  current: Piece,
  candidates: readonly number[]
): number | undefined {
  if (candidates.length <= 1) return candidates[0];

  const incoming = vec.angle(vec.subtract(current.to, current.from));

  let best: number | undefined;
  let bestTurn = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const piece = pieces[candidate];
    if (piece === undefined) continue;

    const outgoing = vec.angle(vec.subtract(piece.to, piece.from));
    // Turn measured in [0, 2π): 0 is straight on, larger values turn further right.
    let turn = incoming - outgoing;
    while (turn < 0) turn += Math.PI * 2;
    while (turn >= Math.PI * 2) turn -= Math.PI * 2;

    if (turn < bestTurn) {
      bestTurn = turn;
      best = candidate;
    }
  }

  return best;
}

/**
 * A position key that tolerates floating-point drift.
 *
 * Two pieces that meet at a computed crossing rarely produce bit-identical coordinates, so
 * joining on exact equality would tear every ring at its intersections.
 */
function keyOf(point: Vec2, epsilon: number): string {
  const grid = Math.max(epsilon, 1e-9);
  return `${String(Math.round(point.x / grid))}:${String(Math.round(point.y / grid))}`;
}

/** Removes repeated consecutive vertices, which would otherwise become zero-length edges. */
function dedupe(p: Polygon, epsilon: number): Polygon {
  const result: Vec2[] = [];
  for (const point of p) {
    const last = result[result.length - 1];
    if (last !== undefined && vec.distance(last, point) <= epsilon) continue;
    result.push(point);
  }
  const first = result[0];
  const last = result[result.length - 1];
  if (
    result.length > 1 &&
    first !== undefined &&
    last !== undefined &&
    vec.distance(first, last) <= epsilon
  ) {
    result.pop();
  }
  return result;
}
